/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { addNoteApi } from "../../api/notes";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Box,
  TextField,
  CircularProgress,
  Tooltip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  fetchEligibleRns,
  fetchMemberCgPlusPrograms,
  deleteAppointment,
  rescheduleAppointment,
  addCalendarEvent,
} from "../../api/scheduling";

const formatLocalDateTime = (date) => {
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const RN_OPTIONS = [
  "Sarah Jenkins, RN ( Assigned )",
  "Michael Chang, RN",
  "Emily Rodriguez, RN",
];

const ASSESSMENT_TYPES = ["Initial Assessment", "Follow-up Assessment"];

const DURATION_OPTIONS = [
  "30 Minutes",
  "45 Minutes",
  "60 Minutes",
  "90 Minutes",
  "Custom...",
];

// All possible slots from 10:00 AM to 06:00 PM
const ALL_SLOTS = [
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "01:00 PM",
  "01:30 PM",
  "02:00 PM",
  "02:30 PM",
  "03:00 PM",
  "03:30 PM",
  "04:00 PM",
  "04:30 PM",
  "05:00 PM",
  "05:30 PM",
  "06:00 PM",
];

const RnVisitSchedulerModal = ({
  open,
  onClose,
  memberId,
  memberCgPlusPrograms = [],
  onScheduleSuccess,
  memberDetails,
}) => {
  const [rnList, setRnList] = useState([]);
  const [selectedRn, setSelectedRn] = useState(null);
  const [assessmentType, setAssessmentType] = useState(ASSESSMENT_TYPES[0]);
  const [cgPlusPrograms, setCgPlusPrograms] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(DURATION_OPTIONS[0]);
  const [customDuration, setCustomDuration] = useState("");
  const [notes, setNotes] = useState("");

  // Calendar State: Default to today
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [selectedTime, setSelectedTime] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [activeManageBooking, setActiveManageBooking] = useState(null);
  const [manageTimeSlot, setManageTimeSlot] = useState("");
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [reschedulingAppointment, setReschedulingAppointment] = useState(null);

  // Custom Dialog States
  const [customConfirmOpen, setCustomConfirmOpen] = useState(false);
  const [customConfirmMessage, setCustomConfirmMessage] = useState("");
  const [customConfirmAction, setCustomConfirmAction] = useState(null);
  const [customAlertOpen, setCustomAlertOpen] = useState(false);
  const [customAlertMessage, setCustomAlertMessage] = useState("");

  const showConfirm = (message, action) => {
    setCustomConfirmMessage(message);
    setCustomConfirmAction(() => action);
    setCustomConfirmOpen(true);
  };

  const showAlert = (message) => {
    setCustomAlertMessage(message);
    setCustomAlertOpen(true);
  };

  const handleConfirmDialogSuccess = () => {
    if (customConfirmAction) {
      customConfirmAction();
    }
    setCustomConfirmOpen(false);
  };

  const getLoggedInUserEmail = () => {
    try {
      const userDetails = JSON.parse(
        localStorage.getItem("userDetails") || "{}",
      );
      return userDetails?.emailId || userDetails?.email || "sdn@lucyrx.com";
    } catch (e) {
      console.error("Failed to parse userDetails from localStorage", e);
      return "sdn@lucyrx.com";
    }
  };

  const getRnEmail = (rnName, memberInfo) => {
    if (rnName.includes("Assigned") && memberInfo?.assigneeEmail) {
      return memberInfo.assigneeEmail;
    }
    if (rnName.includes("Sarah Jenkins")) return getLoggedInUserEmail();
    if (rnName.includes("Michael Chang")) return "mchang@lucyrx.com";
    if (rnName.includes("Emily Rodriguez")) return "erodriguez@lucyrx.com";
    return getLoggedInUserEmail();
  };

  const getSelectedRnEmail = () => {
    if (!selectedRn) return getLoggedInUserEmail();
    if (typeof selectedRn === "string") {
      return getRnEmail(selectedRn, memberDetails);
    }
    return (
      selectedRn.email || selectedRn.employeeEmail || getLoggedInUserEmail()
    );
  };

  const getSelectedRnName = () => {
    if (!selectedRn) return "";
    if (typeof selectedRn === "string") return selectedRn;
    return selectedRn.name || "";
  };

  const getDurationMinutes = () => {
    if (selectedDuration === "Custom...") {
      return Number.parseInt(customDuration) || 30;
    }
    return Number.parseInt(selectedDuration) || 30;
  };

  // Fetch eligible RNs from API
  useEffect(() => {
    const loadRns = async () => {
      try {
        const data = await fetchEligibleRns();
        if (Array.isArray(data) && data.length > 0) {
          setRnList(data);
          setSelectedRn(data[0]);
        } else {
          const fallbackList = RN_OPTIONS.map((name, index) => ({
            id: index + 1,
            name,
            email: getRnEmail(name, memberDetails),
          }));
          setRnList(fallbackList);
          setSelectedRn(fallbackList[0]);
        }
      } catch (err) {
        console.error("Failed to load eligible RNs:", err);
        const fallbackList = RN_OPTIONS.map((name, index) => ({
          id: index + 1,
          name,
          email: getRnEmail(name, memberDetails),
        }));
        setRnList(fallbackList);
        setSelectedRn(fallbackList[0]);
      }
    };
    if (open) {
      void loadRns();
    }
  }, [open, memberDetails]);

  // Fetch enrolled CG+ programs from API
  useEffect(() => {
    const loadPrograms = async () => {
      if (!memberId) return;
      try {
        const data = await fetchMemberCgPlusPrograms(memberId);
        if (Array.isArray(data) && data.length > 0) {
          setCgPlusPrograms(data);
          setSelectedPrograms([data[0].groupName || data[0].name || ""]);
        } else {
          setCgPlusPrograms(memberCgPlusPrograms);
          if (memberCgPlusPrograms.length > 0) {
            setSelectedPrograms([
              memberCgPlusPrograms[0].groupName ||
                memberCgPlusPrograms[0].name ||
                "",
            ]);
          }
        }
      } catch (err) {
        console.error("Failed to load CG+ programs:", err);
        setCgPlusPrograms(memberCgPlusPrograms);
        if (memberCgPlusPrograms.length > 0) {
          setSelectedPrograms([
            memberCgPlusPrograms[0].groupName ||
              memberCgPlusPrograms[0].name ||
              "",
          ]);
        }
      }
    };
    if (open) {
      void loadPrograms();
    }
  }, [open, memberId, memberCgPlusPrograms]);

  // Clear notes when modal opens/closes
  useEffect(() => {
    setNotes("");
  }, [open]);

  // Fetch RN availability
  const fetchAvailability = async () => {
    if (!open || !selectedRn) return;
    setLoadingAvailability(true);
    try {
      const formattedMonth = String(currentMonth + 1).padStart(2, "0");
      const formattedDay = String(selectedDay).padStart(2, "0");
      const startDate = `${currentYear}-${formattedMonth}-${formattedDay}`;

      const email = getLoggedInUserEmail();
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${(process.env.REACT_APP_BASE_URL || "").trim()}/oauth/auth/calendar/view`,
        {
          email,
          startDate,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data && Array.isArray(response.data.value)) {
        setBookedSlots(response.data.value);
      } else if (response?.data?.success && Array.isArray(response.data.data)) {
        setBookedSlots(response.data.data);
      } else {
        setBookedSlots([]);
      }
    } catch (err) {
      console.error("Failed to fetch calendar availability:", err);
      setBookedSlots([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  useEffect(() => {
    void fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRn, currentYear, currentMonth, selectedDay, open, memberDetails]);

  // Retrieve already booked appointments to prevent double booking
  const [localBookings, setLocalBookings] = useState([]);

  useEffect(() => {
    if (open) {
      try {
        const stored = JSON.parse(
          localStorage.getItem("scheduled_rn_visits") || "[]",
        );
        setLocalBookings(stored);
      } catch (e) {
        console.error("Failed to load scheduled visits", e);
      }
    }
  }, [open]);

  // Calendar calculations
  const monthNames = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];

  const daysInMonth = useMemo(() => {
    return new Date(currentYear, currentMonth + 1, 0).getDate();
  }, [currentYear, currentMonth]);

  const firstDayIndex = useMemo(() => {
    return new Date(currentYear, currentMonth, 1).getDay();
  }, [currentYear, currentMonth]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  // Determine if a slot is booked based on RN, Date, Time
  const getSelectedStartDateTime = () => {
    if (!selectedTime) return null;
    const [time, modifier] = selectedTime.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (modifier === "PM" && hours < 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    const start = new Date(
      currentYear,
      currentMonth,
      selectedDay,
      hours,
      minutes,
    );
    return start.toISOString();
  };

  // Get the booking details for a slot if it is booked
  const getSlotBooking = (timeStr) => {
    // Parse timeslot start and end
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (modifier === "PM" && hours < 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    const durationMinutes = getDurationMinutes();

    // 1. Check if slot is booked in the API response (priority)
    if (Array.isArray(bookedSlots) && bookedSlots.length > 0) {
      const apiBook = bookedSlots.find((appt) => {
        if (!appt) return false;

        const apptStartStr =
          appt.start?.dateTime || appt.startTime || appt.start || "";
        const apptEndStr = appt.end?.dateTime || appt.endTime || appt.end || "";

        if (apptStartStr && apptEndStr) {
          // Parse as local/literal date time by extracting components
          // String format: YYYY-MM-DDTHH:mm:ss.sssssss
          const startMatch = apptStartStr.match(
            /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/,
          );
          const endMatch = apptEndStr.match(
            /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/,
          );

          if (startMatch && endMatch) {
            const apptStartLocal = new Date(
              Number.parseInt(startMatch[1]),
              Number.parseInt(startMatch[2]) - 1, // 0-indexed month
              Number.parseInt(startMatch[3]),
              Number.parseInt(startMatch[4]),
              Number.parseInt(startMatch[5]),
              Number.parseInt(startMatch[6]),
            );

            const apptEndLocal = new Date(
              Number.parseInt(endMatch[1]),
              Number.parseInt(endMatch[2]) - 1, // 0-indexed month
              Number.parseInt(endMatch[3]),
              Number.parseInt(endMatch[4]),
              Number.parseInt(endMatch[5]),
              Number.parseInt(endMatch[6]),
            );

            const slotStartLocal = new Date(
              currentYear,
              currentMonth,
              selectedDay,
              hours,
              minutes,
            );
            const slotEndLocal = new Date(
              slotStartLocal.getTime() + durationMinutes * 60 * 1000,
            );

            // Check for overlap in literal local time
            if (
              slotStartLocal < apptEndLocal &&
              slotEndLocal > apptStartLocal
            ) {
              return true;
            }
          }
        }

        // Fallback string matching if start/end properties are missing
        if (typeof appt === "string") {
          return appt.includes(timeStr) || timeStr.includes(appt);
        }
        return false;
      });

      if (apiBook) {
        return typeof apiBook === "string" ? { subject: apiBook } : apiBook;
      }
    }

    // 2. Fallback: Check if it exists in local storage bookings
    const localBook =
      Array.isArray(localBookings) &&
      localBookings.find(
        (b) =>
          b.rn === getSelectedRnName() &&
          b.year === currentYear &&
          b.month === currentMonth &&
          b.day === selectedDay &&
          b.time === timeStr,
      );
    if (localBook) {
      const subject =
        localBook.notes ||
        `${localBook.assessmentType || "RN Visit"} - ${localBook.program || ""}`.trim() ||
        "Scheduled RN Visit";
      return {
        ...localBook,
        subject,
      };
    }

    return null;
  };

  const handleConfirm = async () => {
    if (!selectedPrograms || selectedPrograms.length === 0) {
      showAlert("Please select a program alignment.");
      return;
    }
    if (!selectedTime) {
      showAlert("Please select an available time slot.");
      return;
    }
    if (!notes?.trim()) {
      showAlert("Please enter clinical appointment notes.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const startIsoString = getSelectedStartDateTime();
    const durationMins = getDurationMinutes();
    const rnName = getSelectedRnName();
    const rnEmail = getSelectedRnEmail();

    // Try to find the matching program object to extract its ID
    const programObj = cgPlusPrograms.find((p) =>
      selectedPrograms.includes(p.groupName || p.name),
    );
    const programId = programObj ? programObj.programId || programObj.id : null;

    try {
      const start = new Date(startIsoString);
      const end = new Date(start.getTime() + durationMins * 60 * 1000);

      const meetingAgenda = notes.trim();
      const subject = `${assessmentType} - ${selectedPrograms.join(", ")}`;

      // 1. Call oauth/auth/calendar/add with agenda/notes included in email body
      await addCalendarEvent({
        email: getLoggedInUserEmail(),
        fromDate: formatLocalDateTime(start),
        toDate: formatLocalDateTime(end),
        subject: subject,
        body: meetingAgenda,
        description: meetingAgenda,
        agenda: meetingAgenda,
        notes: meetingAgenda,
      });

      // 2. Call halo/api/notes
      const formattedNote = `RN Visit Scheduled:\nDate/Time: ${currentMonth + 1}/${selectedDay}/${currentYear} @ ${selectedTime}\nAssigned RN: ${rnName}\nProgram: ${selectedPrograms.join(", ")}\nDuration: ${durationMins} Minutes\nPCA Notes: ${meetingAgenda}`;

      await addNoteApi(memberId, {
        type: "General",
        content: formattedNote,
        rating: 1,
        pinned: false,
        programId: Number(programId || 0),
        status: "CREATED",
        archived: false,
        entityType: "MEMBER",
        entityId: String(memberId),
      });

      const appointmentDetails = {
        id: `APT-${Date.now()}`,
        memberId,
        rn: rnName,
        rnEmail,
        assessmentType,
        program: selectedPrograms.join(", "),
        duration: `${durationMins} Minutes`,
        year: currentYear,
        month: currentMonth,
        day: selectedDay,
        time: selectedTime,
        notes: meetingAgenda,
        scheduledAt: new Date().toISOString(),
        memberDetails: memberDetails,
      };

      if (onScheduleSuccess) {
        await onScheduleSuccess(appointmentDetails);
      }
    } catch (err) {
      console.error("Scheduling error:", err);
      setSubmitError(err.message || "Failed to schedule appointment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelAppointment = () => {
    if (!activeManageBooking) return;
    showConfirm(
      "Are you sure you want to cancel this appointment?",
      async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
          const eventId = activeManageBooking.id || activeManageBooking.eventId;

          if (eventId && !String(eventId).startsWith("APT-")) {
            // Real API appointment
            await deleteAppointment({
              email: getLoggedInUserEmail(),
              eventId: eventId,
            });
          }

          // Update local storage
          const stored = JSON.parse(
            localStorage.getItem("scheduled_rn_visits") || "[]",
          );
          const updated = stored.filter(
            (b) =>
              !(
                b.id === eventId ||
                (b.rn === getSelectedRnName() &&
                  b.year === currentYear &&
                  b.month === currentMonth &&
                  b.day === selectedDay &&
                  b.time === manageTimeSlot)
              ),
          );
          localStorage.setItem("scheduled_rn_visits", JSON.stringify(updated));

          // Update local state bookings as well
          setLocalBookings(updated);

          // Refresh calendar availability
          await fetchAvailability();

          showAlert("Appointment cancelled successfully.");
          setActiveManageBooking(null);
        } catch (err) {
          console.error("Cancel API error:", err);
          showAlert(err.message || "Failed to cancel appointment");
        } finally {
          setSubmitting(false);
        }
      },
    );
  };

  const startRescheduling = () => {
    if (!activeManageBooking) return;
    setIsRescheduleMode(true);
    setReschedulingAppointment({
      ...activeManageBooking,
      originalTime: manageTimeSlot,
      year: currentYear,
      month: currentMonth,
      day: selectedDay,
    });
    if (activeManageBooking.notes) {
      setNotes(activeManageBooking.notes);
    }
    setSelectedTime(""); // Clear slot to force new selection
    setActiveManageBooking(null);
  };

  const handleConfirmReschedule = async () => {
    if (!selectedTime) {
      showAlert("Please select a new available time slot.");
      return;
    }
    if (!notes?.trim()) {
      showAlert("Please enter clinical appointment notes.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const eventId =
        reschedulingAppointment.id || reschedulingAppointment.eventId;

      const durationMins = getDurationMinutes();
      const newStart = new Date(currentYear, currentMonth, selectedDay);
      // parse selectedTime e.g., "10:00 AM"
      const [time, modifier] = selectedTime.split(" ");
      let [hours, minutes] = time.split(":").map(Number);
      if (modifier === "PM" && hours < 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;
      newStart.setHours(hours, minutes, 0, 0);

      const newEnd = new Date(newStart.getTime() + durationMins * 60 * 1000);

      const fromDate = formatLocalDateTime(newStart);
      const toDate = formatLocalDateTime(newEnd);
      const meetingAgenda = notes.trim();
      const subject = `${assessmentType || "RN Visit"} - ${selectedPrograms.join(", ")}`;

      if (eventId && !String(eventId).startsWith("APT-")) {
        // Real API reschedule with meeting agenda notes included in email body
        await rescheduleAppointment({
          email: getLoggedInUserEmail(),
          eventId: eventId,
          fromDate,
          toDate,
          subject,
          body: meetingAgenda,
          description: meetingAgenda,
          agenda: meetingAgenda,
          notes: meetingAgenda,
        });
      }

      // 2. Call halo/api/notes on reschedule as well
      const programObj = cgPlusPrograms.find((p) =>
        selectedPrograms.includes(p.groupName || p.name),
      );
      const programId = programObj
        ? programObj.programId || programObj.id
        : null;
      const formattedNote = `RN Visit Rescheduled:\nDate/Time: ${currentMonth + 1}/${selectedDay}/${currentYear} @ ${selectedTime}\nAssigned RN: ${getSelectedRnName()}\nProgram: ${selectedPrograms.join(", ")}\nDuration: ${durationMins} Minutes\nPCA Notes: ${meetingAgenda}`;

      try {
        await addNoteApi(memberId, {
          type: "General",
          content: formattedNote,
          rating: 1,
          pinned: false,
          programId: Number(programId || 0),
          status: "CREATED",
          archived: false,
          entityType: "MEMBER",
          entityId: String(memberId),
        });
      } catch (noteErr) {
        console.error("Failed to post reschedule note:", noteErr);
      }

      // Update local storage if present
      const stored = JSON.parse(
        localStorage.getItem("scheduled_rn_visits") || "[]",
      );
      const updated = stored.map((b) => {
        if (
          b.id === eventId ||
          (b.rn === getSelectedRnName() &&
            b.year === reschedulingAppointment.year &&
            b.month === reschedulingAppointment.month &&
            b.day === reschedulingAppointment.day &&
            b.time === reschedulingAppointment.originalTime)
        ) {
          return {
            ...b,
            year: currentYear,
            month: currentMonth,
            day: selectedDay,
            time: selectedTime,
            notes: meetingAgenda,
            scheduledAt: new Date().toISOString(),
          };
        }
        return b;
      });
      localStorage.setItem("scheduled_rn_visits", JSON.stringify(updated));
      setLocalBookings(updated);

      // Refresh calendar availability
      await fetchAvailability();

      showAlert("Appointment rescheduled successfully.");
      setIsRescheduleMode(false);
      setReschedulingAppointment(null);
    } catch (err) {
      console.error("Reschedule API error:", err);
      setSubmitError(err.message || "Failed to reschedule appointment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAction = () => {
    if (isRescheduleMode) {
      void handleConfirmReschedule();
    } else {
      void handleConfirm();
    }
  };

  const isFormValid =
    !!selectedRn &&
    !!assessmentType &&
    selectedPrograms.length > 0 &&
    (selectedDuration !== "Custom..." || !!customDuration) &&
    !!selectedTime &&
    !!notes &&
    notes.trim() !== "";

  // ---- helper: resolves the tri-state variant for a slot ----

  const getSlotVariant = (isSelected, booked) => {
    if (isSelected) return "selected";
    if (booked) return "booked";
    return "default";
  };

  // ---- color lookup tables (one row per variant, easy to scan/maintain) ----

  const SLOT_BORDER_COLOR = {
    selected: "#403C6F",
    booked: "#F2F2F3",
    default: "#E8E8E9",
  };

  const SLOT_BG_COLOR = {
    selected: "#403C6F",
    booked: "#FAFAFA",
    default: "#FFFFFF",
  };

  const SLOT_TEXT_COLOR = {
    selected: "#FFFFFF",
    booked: "#9F9DB6",
    default: "#10100F",
  };

  const SLOT_HOVER_BG_COLOR = {
    selected: "#403C6F",
    booked: "#FAFAFA",
    default: "#FAFAFF",
  };

  const SLOT_STATUS_TEXT_COLOR = {
    selected: "#B4B2DF",
    booked: "#D13D53",
    default: "#45B184",
  };

  const SLOT_DOT_COLOR = {
    selected: "#FFFFFF",
    booked: "#D13D53",
    default: "#45B184",
  };
  const confirmButtonText = isRescheduleMode ? "Reschedule" : "Schedule";
  const confirmButtonContent = isRescheduleMode
    ? "Reschedule"
    : { confirmButtonText };
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px",
              maxWidth: "800px",
              padding: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E8E8E9",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#10100F" }}>
            Schedule RN Visit
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: "#7F7C9F" }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Form Controls Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
              mb: 3,
              mt: 3,
            }}
          >
            <FormControl fullWidth>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#403C6F", mb: 0.8 }}
              >
                Assigned RN <span style={{ color: "red" }}>*</span>
              </Typography>
              <Select
                value={getSelectedRnName()}
                disabled={isRescheduleMode}
                onChange={(e) => {
                  const found = rnList.find((r) =>
                    typeof r === "string"
                      ? r === e.target.value
                      : r.name === e.target.value,
                  );
                  setSelectedRn(found || e.target.value);
                }}
                size="small"
                sx={{ borderRadius: "8px", bgcolor: "#FAFAFF" }}
              >
                {rnList.map((rn) => {
                  const name = typeof rn === "string" ? rn : rn.name;
                  return (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#403C6F", mb: 0.8 }}
              >
                Clinical Assessment Type <span style={{ color: "red" }}>*</span>
              </Typography>
              <Select
                value={assessmentType}
                disabled={isRescheduleMode}
                onChange={(e) => setAssessmentType(e.target.value)}
                size="small"
                sx={{ borderRadius: "8px", bgcolor: "#FAFAFF" }}
              >
                {ASSESSMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{type}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#403C6F", mb: 0.8 }}
              >
                Select Program <span style={{ color: "red" }}>*</span>
              </Typography>
              <Select
                multiple
                value={selectedPrograms}
                disabled={isRescheduleMode}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedPrograms(
                    typeof val === "string" ? val.split(",") : val,
                  );
                }}
                renderValue={(selected) => selected.join(", ")}
                size="small"
                sx={{ borderRadius: "8px", bgcolor: "#FAFAFF" }}
              >
                {cgPlusPrograms.length === 0 ? (
                  <MenuItem value="" disabled>
                    No eligible CG+ programs
                  </MenuItem>
                ) : (
                  cgPlusPrograms.map((prog) => {
                    const name = prog.groupName || prog.name;
                    return (
                      <MenuItem
                        key={prog.id || prog.programId || name}
                        value={name}
                      >
                        <Checkbox
                          checked={selectedPrograms.includes(name)}
                          sx={{
                            color: "#403C6F",
                            "&.Mui-checked": {
                              color: "#403C6F",
                            },
                          }}
                        />
                        <ListItemText primary={name} />
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#403C6F", mb: 0.8 }}
              >
                Visit Duration <span style={{ color: "red" }}>*</span>
              </Typography>
              <Select
                value={selectedDuration}
                disabled={isRescheduleMode}
                onChange={(e) => {
                  setSelectedDuration(e.target.value);
                  if (e.target.value !== "Custom...") {
                    setCustomDuration("");
                  }
                }}
                size="small"
                sx={{ borderRadius: "8px", bgcolor: "#FAFAFF" }}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
              {selectedDuration === "Custom..." && (
                <TextField
                  type="number"
                  size="small"
                  disabled={isRescheduleMode}
                  placeholder="Enter minutes (e.g. 50)"
                  value={customDuration}
                  onChange={(e) => setCustomDuration(e.target.value)}
                  sx={{ mt: 1.5, borderRadius: "8px", bgcolor: "#FAFAFF" }}
                  slotProps={{
                    htmlInput: { min: 1 },
                  }}
                />
              )}
            </FormControl>
          </Box>

          {/* Selected Date & Time View */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              border: "1px solid #E8E8E9",
              borderRadius: "8px",
              backgroundColor: "#FAFAFF",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: "#403C6F" }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "#403C6F" }}
              >
                Scheduled Date & Time
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, color: "#403C6F" }}
            >
              {selectedTime
                ? `${String(currentMonth + 1).padStart(2, "0")}/${String(selectedDay).padStart(2, "0")}/${currentYear} @ ${selectedTime}`
                : "N/A"}
            </Typography>
          </Box>

          {isRescheduleMode && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: "#FAECED",
                color: "#A83B49",
                border: "1px solid #F3C3C9",
                borderRadius: "8px",
                p: 2,
                mb: 3,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Rescheduling appointment originally set for{" "}
                {reschedulingAppointment
                  ? `${String(reschedulingAppointment.month + 1).padStart(2, "0")}/${String(reschedulingAppointment.day).padStart(2, "0")}`
                  : ""}{" "}
                @ {reschedulingAppointment?.originalTime}. Please select a new
                target date and time below.
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  setIsRescheduleMode(false);
                  setReschedulingAppointment(null);
                }}
                sx={{
                  textTransform: "none",
                  color: "#A83B49",
                  fontWeight: 700,
                }}
              >
                Cancel Rescheduling
              </Button>
            </Box>
          )}

          {/* Calendar and Slots Flex Container */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              mb: 3,
            }}
          >
            {/* Calendar Picker Card */}
            <Box
              sx={{
                flex: 1,
                border: "1px solid #E8E8E9",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "#10100F" }}
                >
                  {monthNames[currentMonth]} {currentYear}
                </Typography>
                <Box>
                  <IconButton
                    onClick={handlePrevMonth}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton onClick={handleNextMonth} size="small">
                    <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Calendar Weekday Names */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  textAlign: "center",
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "#7F7C9F",
                  mb: 1,
                }}
              >
                <div>Su</div>
                <div>Mo</div>
                <div>Tu</div>
                <div>We</div>
                <div>Th</div>
                <div>Fr</div>
                <div>Sa</div>
              </Box>

              {/* Calendar Grid Cells */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  rowGap: 1.5,
                  columnGap: 1,
                }}
              >
                {/* Blank leading days */}
                {Array.from({ length: firstDayIndex }).map((_, i) => {
                  const day = i + 1;
                  return <div key={`empty-${day}`} />;
                })}

                {/* Month Days */}
                {Array.from({ length: daysInMonth }).map((_, idx) => {
                  const day = idx + 1;
                  const isSelected = selectedDay === day;
                  return (
                    <Box
                      key={`day-${day}`}
                      onClick={() => {
                        setSelectedDay(day);
                        // Clear timeslot selection when date changes to prompt selection
                        setSelectedTime("");
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "36px",
                        width: "36px",
                        mx: "auto",
                        borderRadius: "50%",
                        fontSize: "14px",
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected ? "#FFFFFF" : "#10100F",
                        bgcolor: isSelected ? "#403C6F" : "transparent",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: isSelected ? "#403C6F" : "#ECECF1",
                        },
                      }}
                    >
                      {day}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Timeslots Panel */}
            <Box
              sx={{
                width: { xs: "100%", md: "280px" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "#10100F", mb: 1.5, mt: 0.5 }}
              >
                Available Slots
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                  maxHeight: "260px",
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                {loadingAvailability ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 8,
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: "#403C6F" }} />
                  </Box>
                ) : (
                  ALL_SLOTS.map((timeStr) => {
                    const booking = getSlotBooking(timeStr);
                    const booked = !!booking;
                    const isSelected = selectedTime === timeStr;
                    const slotVariant = getSlotVariant(isSelected, booked);

                    const slotElement = (
                      <Box
                        onClick={() => {
                          if (booked) {
                            setActiveManageBooking(booking);
                            setManageTimeSlot(timeStr);
                            setSelectedTime(timeStr); // Highlight selected
                          } else {
                            setActiveManageBooking(null);
                            setSelectedTime(timeStr);
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 2,
                          py: 1.2,
                          borderRadius: "8px",
                          border: "1px solid",
                          borderColor: SLOT_BORDER_COLOR[slotVariant],
                          bgcolor: SLOT_BG_COLOR[slotVariant],
                          cursor: "pointer",
                          color: SLOT_TEXT_COLOR[slotVariant],
                          opacity: booked ? 0.8 : 1,
                          "&:hover": {
                            borderColor: "#403C6F",
                            bgcolor: SLOT_HOVER_BG_COLOR[slotVariant],
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {timeStr}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "10px",
                              color: SLOT_STATUS_TEXT_COLOR[slotVariant],
                            }}
                          >
                            {booked ? "BOOKED / MANAGE" : "Avaliable"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            bgcolor: SLOT_DOT_COLOR[slotVariant],
                          }}
                        />
                      </Box>
                    );

                    return booked ? (
                      <Tooltip
                        key={timeStr}
                        title={
                          booking?.subject ||
                          booking?.notes ||
                          "Booked / Unavailable"
                        }
                        placement="top"
                        arrow
                      >
                        <span style={{ display: "block" }}>{slotElement}</span>
                      </Tooltip>
                    ) : (
                      <React.Fragment key={timeStr}>
                        {slotElement}
                      </React.Fragment>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>

          {/* PCA Notes */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#403C6F",
                mb: 0.8,
                display: "block",
              }}
            >
              Clinical Appointment Notes <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder="Record patient goals, home checklist reminders, or pre-visit diagnostic files checklist..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: "8px",
                  fontSize: "14px",
                  bgcolor: "#FFFFFF",
                },
              }}
            />
          </Box>
          {submitError && (
            <Typography
              variant="body2"
              sx={{ color: "#D13D53", mt: 1.5, fontWeight: 500 }}
            >
              {submitError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            borderTop: "1px solid #E8E8E9",
            pt: 2,
            pb: 2,
            px: 3,
          }}
        >
          {activeManageBooking ? (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancelAppointment}
                disabled={submitting}
                sx={{
                  borderRadius: "24px",
                  textTransform: "none",
                  px: 3,
                }}
              >
                Cancel Visit
              </Button>
              <Button
                variant="contained"
                onClick={startRescheduling}
                disabled={submitting}
                sx={{
                  borderRadius: "24px",
                  bgcolor: "#403C6F",
                  color: "#FFFFFF",
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    bgcolor: "#2F2B59",
                  },
                }}
              >
                Reschedule
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={submitting}
                sx={{
                  borderRadius: "24px",
                  borderColor: "#E8E8E9",
                  color: "#403C6F",
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    bgcolor: "#F7F8FB",
                    borderColor: "#403C6F",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmAction}
                disabled={!isFormValid || submitting}
                sx={{
                  borderRadius: "24px",
                  bgcolor: "#403C6F",
                  color: "#FFFFFF",
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    bgcolor: "#2F2B59",
                  },
                }}
              >
                {submitting ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    <span>Scheduling...</span>
                  </Box>
                ) : (
                  confirmButtonContent
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Custom Confirmation Dialog */}
      <Dialog
        open={customConfirmOpen}
        onClose={() => setCustomConfirmOpen(false)}
        slotProps={{
          paper: {
            sx: { borderRadius: "16px", p: 1, maxWidth: "400px" },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Confirm Action
        </DialogTitle>
        <DialogContent sx={{ minWidth: "300px" }}>
          <Typography variant="body2">{customConfirmMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setCustomConfirmOpen(false)}
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDialogSuccess}
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Alert Dialog */}
      <Dialog
        open={customAlertOpen}
        onClose={() => setCustomAlertOpen(false)}
        slotProps={{
          paper: {
            sx: { borderRadius: "16px", p: 1, maxWidth: "400px" },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Notification</DialogTitle>
        <DialogContent sx={{ minWidth: "300px" }}>
          <Typography variant="body2">{customAlertMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setCustomAlertOpen(false)}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              bgcolor: "#403C6F",
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

RnVisitSchedulerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  memberId: PropTypes.string,
  memberCgPlusPrograms: PropTypes.array,
  onScheduleSuccess: PropTypes.func,
  memberDetails: PropTypes.object,
};

export default RnVisitSchedulerModal;
